import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, use, useMemo, type ReactNode } from 'react';
import { apiClient } from 'shared/api-client';
import { useAuthTokens, useDecodedAccessToken, type Tokens } from 'shared/tokens';

import { useLogout } from '@/lib/api/mutations.ts';
import { authInitQuery } from '@/lib/api/queries.ts';
import { InAppGateContext } from '@/lib/in-app-gate.tsx';
import { hardNavigate } from '@/lib/utils.ts';

import { applyConsents } from '../Http/mutation-fns.ts';

export interface AuthContextValue {
  login: (method: 'BANK_ID' | 'VIPPS' | 'SCRIVE', returnTo?: string | null) => Promise<void>;
  logout: () => Promise<void>;
  /** @deprecated use `applyConsents` from `methods.ts` */
  acceptConsents: (consents: string[]) => Promise<void>;
  token: Tokens | undefined;
  /** @deprecated use `isAuthInit` directly */
  authData: AuthDataState;
  userId: string | undefined;
  isAnonymous: boolean;
  isAuthInit: boolean;
  analytics_user_id: string | undefined;
  analytics_session_id: string | undefined;
}

type AuthDataState = {
  isAuthInit: boolean;
};

// oxlint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue>(undefined!);

export const AuthProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const tokens = useAuthTokens();
  const decodedToken = useDecodedAccessToken();
  const { mutateAsync: logout } = useLogout();

  const { data: initData } = useQuery(authInitQuery());
  const { isInAppBrowser } = use(InAppGateContext);

  const isAuthInit = Boolean(initData);
  const userId = initData?.userId;

  // This is an object for historical reasons, it should be refactored later
  const authData: AuthDataState = useMemo(() => ({ isAuthInit }), [isAuthInit]);

  const isAnonymous = decodedToken?.is_anonymous ?? true;

  /** @deprecated to be removed */
  const acceptConsents = async (consents: string[]) => {
    if (tokens) {
      try {
        await applyConsents(consents);
        await applyConsents.invalidateCache(queryClient);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const login = async (method: 'BANK_ID' | 'VIPPS' | 'SCRIVE', returnTo?: string | null) => {
    if (await isInAppBrowser()) {
      return;
    }
    try {
      const res = await apiClient
        .get('account/login/', {
          searchParams: {
            authentication_provider: method || 'SCRIVE', // For compatibility with js code
            ...(returnTo && { return_to: returnTo }),
          },
        })
        .json<{ url?: string }>();

      if (res?.url) {
        await hardNavigate(res.url);
      }
    } catch (e) {
      await logout();
      console.error(e);
    }
  };

  const value: AuthContextValue = {
    acceptConsents,
    login,
    logout,
    token: tokens,
    authData,
    userId,
    isAnonymous,
    isAuthInit,
    analytics_user_id: decodedToken?.analytics_user_id,
    analytics_session_id: decodedToken?.analytics_session_id,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
